import { useState } from "react";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import moment from "moment";
import { useNavigate } from "react-router-dom";

export default function PaymentsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [page] = useState(1);
  const [patientFilter, setPatientFilter] = useState("");

  const queryParams = new URLSearchParams();
  queryParams.set("page", page);
  queryParams.set("page_size", "50");
  if (patientFilter) queryParams.set("patient", patientFilter);

  const { data, isLoading, isError, error } = useFetchObjects(
    ["payments", page, patientFilter],
    `payments/?${queryParams.toString()}`,
    token
  );

  const payments = data?.results || data || [];

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || "Failed to load payments"}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {t("finance.payments", { defaultValue: "Payments" })}
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container spacing={2} justifyContent="flex-end" alignItems="center">
              <Grid item>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/finance/payments/add")}>
                  {t("finance.addPayment", { defaultValue: "Add Payment" })}
                </Button>
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  label={t("finance.patientId", { defaultValue: "Patient ID" })}
                  value={patientFilter}
                  onChange={(e) => setPatientFilter(e.target.value)}
                  placeholder="ID"
                  sx={{ minWidth: 120 }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.200" }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>{t("finance.patient")}</strong></TableCell>
                  <TableCell><strong>{t("finance.date")}</strong></TableCell>
                  <TableCell><strong>{t("finance.amount")}</strong></TableCell>
                  <TableCell><strong>{t("finance.method")}</strong></TableCell>
                  <TableCell><strong>{t("finance.receipt")}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      {t("finance.noRecords", { defaultValue: "No payments found." })}
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.patient_name_display || `#${row.patient}`}</TableCell>
                      <TableCell>{row.payment_date ? moment(row.payment_date).format("YYYY-MM-DD HH:mm") : "—"}</TableCell>
                      <TableCell>{row.amount != null ? Number(row.amount).toFixed(2) : "—"}</TableCell>
                      <TableCell>{row.payment_method_display || "—"}</TableCell>
                      <TableCell>{row.receipt_number || "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
